import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import List from '@mui/material/List';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import Image from 'components/Image';
import { useNavigate } from 'react-router-dom';
import { handleErrorResponse } from 'utils/general';
import { useMenu } from 'contexts/menu';
import { initialAuthState, useAuth } from 'contexts/auth';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { ListItem, ListItemButton, ListItemText, Collapse } from '@mui/material';
import { logout } from 'api/auth';
import { useTranslation } from 'react-i18next';
import { getPublicCollectionDefinition } from 'api/collection_definitions';
// eslint-disable-next-line import/no-cycle
import { LANGUAGE_COLLECTION_DEFINITION_ID } from 'pages/AppSummary/LanguageSection';
import { getPublicDynamicTableRows } from 'api/sections';
import useViewportLock from 'utils/viewportLock';
import { getDynamicData } from 'utils/sections';
import get from 'lodash/get';
import find from 'lodash/find';
import clone from 'lodash/clone';

export const DEFAULT_MENU_HEADER_HEIGHT = 100;
// Height of the slim top bar pinned at the top of mobile pages. Kept
// separate from DEFAULT_MENU_HEADER_HEIGHT, which the drawer profile
// header still uses.
export const MOBILE_MENU_BAR_HEIGHT = 60;

const WatchOneMobilePageHeaderSection = ({
  primary_header_background_color,
  header_logo_url,
  secondary_header_background_color,
  default_user_profile_image_url,
  menu_items,
  font_color,
  log_path,
  monitor_path,
  ...props }) => {
  const navigate = useNavigate();
  const { isOpen, setOpen } = useMenu();
  const { auth, setAuth } = useAuth();
  const [languageData, setLanguageData] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState();
  const { i18n: { changeLanguage, language } } = useTranslation();
  const userRoleScopes = useMemo(() => auth.user?.role?.scopes || [], [auth.user]);
  const [menuOpenStatusObject, setMenuOpenStatusObject] = useState({});

  useEffect(() => {
    const targetLanguage = languageData.find((datum) => datum.value === language);
    setCurrentLanguage(targetLanguage?.name);
  }, [language, languageData]);

  const handleLogout = useCallback(() => {
    return logout()
      .then(() => {
        setAuth(initialAuthState);
        navigate('/login');
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [navigate, setAuth]);

  const fetchLanguageFromApi = useCallback(() => {
    return getPublicCollectionDefinition(LANGUAGE_COLLECTION_DEFINITION_ID, { $expand: 'attributes' })
      .then((res) => {
        const { attributes } = res.data;
        return getPublicDynamicTableRows(LANGUAGE_COLLECTION_DEFINITION_ID)
          .then(({ data }) => {
            const dynamicFieldValueData = data.map((datum) => ({
              ...getDynamicData(datum, attributes),
              ...datum,
            }));
            setLanguageData(dynamicFieldValueData);
          })
          .catch((err) => {
            handleErrorResponse(err, setAuth);
          });
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [setAuth]);

  const handleChangeLanguage = () => {
    const currentLanguageIndex = languageData.findIndex((datum) => datum.value === language);
    let newLanguageIndex = currentLanguageIndex + 1;
    if (newLanguageIndex >= languageData.length) {
      newLanguageIndex = 0;
    }
    const newLanguage = languageData[newLanguageIndex].value;
    localStorage.setItem('i18nextLng', newLanguage);
    changeLanguage(newLanguage);
    window.location.reload();
  };

  useEffect(() => {
    fetchLanguageFromApi();
  }, [fetchLanguageFromApi]);

  const userProfileImageUrl = auth?.user?.profile_image?.file_url;
  const avatarStyle = {
    position: 'relative',
    flexShrink: 0,
    borderRadius: '50%',
    backgroundImage: `url(${userProfileImageUrl || default_user_profile_image_url})`,
    backgroundPosition: 'center',
    // a portrait fills the circle; the wide default logo is drawn at a
    // fraction of it so it sits inside the circle with room to spare
    backgroundSize: userProfileImageUrl ? 'cover' : '65%',
    backgroundRepeat: 'no-repeat',
  };

  let drawer;
  if (find(userRoleScopes, ['scope', 'watch_admin']) == null) {
    // non-admin user menu bar
    drawer = (<Drawer
      variant="temporary"
      anchor="left"
      open={isOpen}
      onClose={() => setOpen(false)}
      sx={{
        'width': '100vw',
        'flexShrink': 0,
        '& .MuiDrawer-paper': {
          width: '100vw',
          boxSizing: 'border-box',
        },
      }}
      {...props}
    >
      <MKBox
        display="flex"
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="center"
        bgColor={secondary_header_background_color}
        sx={{
          height: DEFAULT_MENU_HEADER_HEIGHT,
          minHeight: DEFAULT_MENU_HEADER_HEIGHT,
          padding: '5rem 2rem',
        }}
      >
        <CloseIcon fontSize="medium" onClick={() => { setOpen(false); }} style={{ position: 'absolute', top: '10px', left: '10px', cursor: 'pointer' }} />
        <MKBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="150px"
          height="150px"
          minWidth="150px"
          sx={avatarStyle}
        />
        {auth?.user && (
          <MKBox ml="1rem" sx={{ minWidth: 0 }}>
            <MKTypography
              fontSize="30px"
              color={font_color}
            >
              {auth?.user.display_name}
            </MKTypography>
            <MKTypography
              fontSize="20px"
              color="gray"
              style={{ marginTop: '-0.5rem', wordBreak: 'break-word' }}
            >
              {auth?.user.username}
            </MKTypography>
          </MKBox>
        )}
      </MKBox>
      <List style={{ marginTop: '1rem' }}>
        {(menu_items || []).map((item) => {
          const { label, path, scope } = item;
          const hasAccess = !scope || !!userRoleScopes.find((roleScope) => roleScope.scope === scope);
          if (!hasAccess) {
            return null;
          }
          let icon;
          switch (label.toLowerCase()) {
            case 'monitors':
              icon = <NotificationsNoneOutlinedIcon fontSize="large" style={{ marginRight: '1rem', color: font_color }} />;
              break;
            case 'edit profile':
              icon = <PersonIcon fontSize="large" style={{ marginRight: '1rem', color: font_color }} />;
              break;
            case 'language':
              icon = <LanguageIcon fontSize="large" style={{ marginRight: '1rem', color: font_color }} />;
              break;
            case 'sign out':
              icon = <LogoutIcon fontSize="large" style={{ marginRight: '1rem', color: font_color }} />;
              break;
            default:
              break;
          }
          const lowerCaseLabel = label.toLowerCase();
          if (lowerCaseLabel.includes('product') || lowerCaseLabel.includes('application') || lowerCaseLabel.includes('iwatch') || lowerCaseLabel.includes('itrack')) {
            icon = <LibraryBooksOutlinedIcon fontSize="large" style={{ marginRight: '1rem', color: font_color }} />;
          }
          let handleOnClick;
          if (path) {
            handleOnClick = () => {
              navigate(path);
              setOpen(false);
            };
          } else if (label.toLowerCase() === 'language') {
            handleOnClick = () => handleChangeLanguage();
          } else if (label.toLowerCase() === 'sign out') {
            handleOnClick = () => handleLogout();
          }
          return (
            <MKBox>
              <ListItem disablePadding>
                <MKBox
                  width="100%"
                  color={font_color}
                >
                  <ListItemButton
                    alignItems="center"
                    display="flex"
                    onClick={handleOnClick}
                    width="100%"
                    style={{
                      justifyContent: 'space-between',
                    }}
                  >
                    <MKBox display="flex" alignItems="center">
                      {icon}
                      <MKTypography color={font_color}>
                        {label}
                      </MKTypography>
                    </MKBox>
                    {path && <ArrowForwardIosIcon />}
                    {label.toLowerCase() === 'language' && currentLanguage}
                  </ListItemButton>
                </MKBox>
              </ListItem>
              <Divider variant="inset" component="li" />
            </MKBox>
          );
        })}
      </List>
    </Drawer>);
  } else {
    // admin user menu bar
    menu_items = [{
      "label": "Select Applications",
      // "path": "/applications",
      "scope": "watch_admin",
      "children": [{
        "label": "iWatch",
        "path": "/live_products"
      }, {
        "label": "iTrack",
        "path": "/tracking_dashboard",
        "scope": "watch_admin"
      }]
    }, {
      "label": "Admin Panel",
      "children": [{
        "label": "User Management",
        "path": "/users",
        "scope": "watch_admin"
      }, {
        "label": "iWatch Management",
        "scope": "watch_admin",
        "children": [{
          "label": "Product Management",
          "path": "/product_management",
          "scope": "watch_admin"
        }, {
          "label": "Contact Management",
          "path": "/contact_management",
          "scope": "watch_admin"
        }, {
          "label": "Uninterested Products",
          "path": "/uninterested_products",
          "scope": "watch_admin"
        }, {
          "label": "Incomplete Products",
          "path": "/incomplete_products",
          "scope": "watch_admin"
        }, {
          "label": "Configurations",
          "path": "/admin_configurations",
          "scope": "watch_admin"
        }, {
          "label": "Source information",
          "path": "/source_information",
          "scope": "watch_admin"
        }, {
          "label": "Product statistics",
          "path": "/product_statistics",
          "scope": "watch_admin"
        }]
      }, {
        "label": "iTrack Management",
        "scope": "watch_admin",
        "children": [{
          "label": "Configurations",
          "path": "/configurations",
          "scope": "watch_admin"
        }]
      }]
    }, {
      "label": "Edit Profile",
      "path": "/edit_profile"
    }, {
      "label": "Sign Out"
    }];

    const renderSubItem = (subMenuItem, parentKey, level) => {
      if (!subMenuItem) {
        return null;
      }
      level = level || 0;
      ++level;
      const label = subMenuItem.label;
      const key = parentKey + '_' + level + '_' + label;
      const paddingLeft = level * 4;
      const children = subMenuItem.children;
      const hasChildren = children && children.length > 0;
      const parentOpenStatus = get(menuOpenStatusObject, parentKey);
      const currentOpenStatus = get(menuOpenStatusObject, key);
      const path = subMenuItem.path;
      let handleClick;
      if (hasChildren) {
        handleClick = () => {
          const newMenuOpenStatusObject = clone(menuOpenStatusObject);
          newMenuOpenStatusObject[key] = !menuOpenStatusObject[key];
          setMenuOpenStatusObject(newMenuOpenStatusObject);
        }
      } else if (path) {
        handleClick = () => {
          navigate(path);
          setOpen(false);
        };
      }
      return (
        <Collapse key={key} in={parentOpenStatus}>
          <List component="div" disablePadding>
            <ListItemButton sx={{ pl: paddingLeft }} onClick={handleClick}>
              <ListItemText primary={subMenuItem.label} />
              {path && <ArrowForwardIosIcon />}
              {hasChildren && (currentOpenStatus ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
            </ListItemButton>
            {hasChildren && children.map(child => {
              return renderSubItem(child, key, level);
            })}
          </List>
        </Collapse>
      )
    };
    drawer = (<Drawer
      variant="temporary"
      anchor="left"
      open={isOpen}
      onClose={() => setOpen(false)}
      sx={{
        'width': '100vw',
        'flexShrink': 0,
        '& .MuiDrawer-paper': {
          width: '100vw',
          boxSizing: 'border-box',
        },
      }}
      {...props}
    >
      <MKBox
        display="flex"
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="center"
        bgColor={secondary_header_background_color}
        sx={{
          height: DEFAULT_MENU_HEADER_HEIGHT,
          minHeight: DEFAULT_MENU_HEADER_HEIGHT,
          padding: '5rem 2rem',
        }}
      >
        <CloseIcon fontSize="medium" onClick={() => { setOpen(false); }} style={{ position: 'absolute', top: '10px', left: '10px', cursor: 'pointer' }} />
        <MKBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="150px"
          height="150px"
          minWidth="150px"
          sx={avatarStyle}
        />
        {auth?.user && (
          <MKBox ml="1rem" sx={{ minWidth: 0 }}>
            <MKTypography
              fontSize="30px"
              color={font_color}
            >
              {auth?.user.display_name}
            </MKTypography>
            <MKTypography
              fontSize="20px"
              color="gray"
              style={{ marginTop: '-0.5rem', wordBreak: 'break-word' }}
            >
              {auth?.user.username}
            </MKTypography>
          </MKBox>
        )}
      </MKBox>
      <List style={{ marginTop: '1rem' }}>
        {(menu_items || []).map((item) => {
          const { label, path, scope } = item;
          const hasAccess = !scope || !!userRoleScopes.find((roleScope) => roleScope.scope === scope);
          if (!hasAccess) {
            return null;
          }
          let icon;
          switch (label.toLowerCase()) {
            case 'monitors':
              icon = <NotificationsNoneOutlinedIcon fontSize="large" style={{ marginRight: '1rem', color: font_color }} />;
              break;
            case 'edit profile':
              icon = <PersonIcon fontSize="large" style={{ marginRight: '1rem', color: font_color }} />;
              break;
            case 'language':
              icon = <LanguageIcon fontSize="large" style={{ marginRight: '1rem', color: font_color }} />;
              break;
            case 'admin panel':
              icon = <DashboardIcon fontSize="large" style={{ marginRight: '1rem', color: font_color }} />;
              break;
            case 'sign out':
              icon = <LogoutIcon fontSize="large" style={{ marginRight: '1rem', color: font_color }} />;
              break;
            default:
              break;
          }
          const lowerCaseLabel = label.toLowerCase();
          if (lowerCaseLabel.includes('product') || lowerCaseLabel.includes('application') || lowerCaseLabel.includes('iwatch') || lowerCaseLabel.includes('itrack')) {
            icon = <LibraryBooksOutlinedIcon fontSize="large" style={{ marginRight: '1rem', color: font_color }} />;
          }
          const key = label;
          const children = item.children;
          const hasChildren = children && children.length > 0;
          const currentOpenStatus = get(menuOpenStatusObject, key);
          let handleOnClick;
          if (hasChildren) {
            handleOnClick = () => {
              const newMenuOpenStatusObject = clone(menuOpenStatusObject);
              newMenuOpenStatusObject[key] = !menuOpenStatusObject[key];
              setMenuOpenStatusObject(newMenuOpenStatusObject);
            }
          } else if (path) {
            handleOnClick = () => {
              navigate(path);
              setOpen(false);
            };
          } else if (label.toLowerCase() === 'language') {
            handleOnClick = () => handleChangeLanguage();
          } else if (label.toLowerCase() === 'sign out') {
            handleOnClick = () => handleLogout();
          }

          return (
            <MKBox key={key}>
              <ListItem disablePadding>
                <MKBox
                  width="100%"
                  color={font_color}
                >
                  <ListItemButton
                    alignItems="center"
                    display="flex"
                    onClick={handleOnClick}
                    width="100%"
                    style={{
                      justifyContent: 'space-between',
                    }}
                  >
                    <MKBox display="flex" alignItems="center">
                      {icon}
                      <MKTypography color={font_color}>
                        {label}
                      </MKTypography>
                    </MKBox>
                    {path && <ArrowForwardIosIcon />}
                    {hasChildren && (currentOpenStatus ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                    {label.toLowerCase() === 'language' && currentLanguage}

                  </ListItemButton>
                </MKBox>
              </ListItem>
              {hasChildren && children.map(child => {
                return renderSubItem(child, key, 0);
              })}
              <Divider variant="inset" component="li" />
            </MKBox>
          );
        })}
      </List>
    </Drawer>);
  }

  // the document must not scroll on these pages: see utils/viewportLock
  useViewportLock();

  return (
    <MKBox id="bar">
      {drawer}
      {/* In flow, not fixed or sticky: the page scrolls inside the box below
          this bar (pages/Universal), so the bar stays on screen because it is
          outside that box -- nothing for a compositor to position against a
          viewport whose size the browser is still animating. */}
      <MKBox display="flex" width="100%" height={MOBILE_MENU_BAR_HEIGHT} alignItems="center" padding="0 1rem" style={{ backgroundColor: primary_header_background_color, position: 'relative', zIndex: 15 }}>
        <MKBox display="flex" flex="1" alignItems="center">
          <MenuOutlinedIcon onClick={() => setOpen(true)} fontSize="medium" color="white" style={{ cursor: 'pointer' }} />
        </MKBox>
        <MKBox display="flex" flex="1" alignItems="center" justifyContent="center">
          <Image src={header_logo_url} objectFit="contain" style={{ maxHeight: MOBILE_MENU_BAR_HEIGHT - 16, maxWidth: '100%' }} />
        </MKBox>
        <MKBox display="flex" flex="1" alignItems="center" justifyContent="flex-end">
          <NotificationsNoneOutlinedIcon fontSize="medium" color="white" style={{ cursor: 'pointer', marginRight: '1rem' }} onClick={() => navigate(monitor_path)} />
          <LibraryBooksOutlinedIcon fontSize="medium" color="white" style={{ cursor: 'pointer' }} onClick={() => navigate(log_path)} />
        </MKBox>
      </MKBox>
    </MKBox>
  );
};

WatchOneMobilePageHeaderSection.propTypes = {
  primary_header_background_color: PropTypes.string,
  header_logo_url: PropTypes.string,
  secondary_header_background_color: PropTypes.string,
  default_user_profile_image_url: PropTypes.string,
  menu_items: PropTypes.array,
  font_color: PropTypes.string,
  log_path: PropTypes.string,
  monitor_path: PropTypes.string,
};

WatchOneMobilePageHeaderSection.defaultProps = {
  primary_header_background_color: '',
  header_logo_url: '',
  secondary_header_background_color: '',
  default_user_profile_image_url: '',
  menu_items: [],
  font_color: '',
  log_path: '',
  monitor_path: '',
};

export default WatchOneMobilePageHeaderSection;
